import { useCallback, useEffect, useState } from "react";
import { fetchLatestBuildId, getRunningBuildId } from "~/lib/app-version";

/** Slow poll while the tab is visible. A deploy is not an emergency. */
export const VERSION_CHECK_INTERVAL_MS = 10 * 60_000;
/**
 * No two checks closer than this, whatever triggers them — waking the tab fires
 * visibilitychange, focus and online almost together, and flicking between
 * tabs must not turn into a request per flick.
 */
export const VERSION_CHECK_MIN_GAP_MS = 60_000;

/** Dispatched by the inline boot-recovery script (app/boot-recovery.ts). */
const STALE_BUILD_EVENT = "palate:stale-build";

export type NewVersionReason =
  /** version.json names a different build than the one this tab runs. */
  | "new-version"
  /** A chunk of this build failed to load — what a replaced build looks like from inside. */
  | "load-failure";

interface NewVersionState {
  /** Why the user is being offered a reload, or null when there is nothing to say. */
  reason: NewVersionReason | null;
  dismiss: () => void;
}

/**
 * Bug-255 — a tab left open across a deploy keeps running the old build until
 * something it needs has been deleted, and then misbehaves with nothing on
 * screen to say why. This is the "say so first" half: it notices that a newer
 * build is live (when the tab wakes and on a slow interval) or that part of
 * this one failed to load, and reports it as a reason to offer a Reload.
 *
 * It never reloads anything itself: the page may hold a half-filled form.
 */
export function useNewVersionAvailable(): NewVersionState {
  const [newBuildLive, setNewBuildLive] = useState(false);
  const [loadFailed, setLoadFailed] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  // The inline script may have announced a failure before React mounted this.
  // Read in an effect, not in useState's initializer: this tree is prerendered
  // at build time, where there is no window, and the first client render must
  // match it.
  useEffect(() => {
    const announcedEarly = (
      window as unknown as { __palateStaleBuild?: boolean }
    ).__palateStaleBuild;
    if (announcedEarly) setLoadFailed(true);

    const onStaleBuild = () => {
      setLoadFailed(true);
      // Something new went wrong; an earlier "not now" does not cover it.
      setDismissed(false);
    };
    window.addEventListener(STALE_BUILD_EVENT, onStaleBuild);
    return () => window.removeEventListener(STALE_BUILD_EVENT, onStaleBuild);
  }, []);

  useEffect(() => {
    const running = getRunningBuildId();
    if (!running || newBuildLive) return;

    let lastCheckAt = 0;
    let stopped = false;
    let inFlight: AbortController | null = null;

    const check = async () => {
      if (stopped || inFlight) return;
      if (lastCheckAt && Date.now() - lastCheckAt < VERSION_CHECK_MIN_GAP_MS) return;
      lastCheckAt = Date.now();

      const controller = new AbortController();
      inFlight = controller;
      const latest = await fetchLatestBuildId(controller.signal);
      inFlight = null;

      if (!stopped && latest && latest !== running) {
        setNewBuildLive(true);
        setDismissed(false);
      }
    };

    const checkIfVisible = () => {
      if (document.visibilityState === "visible") void check();
    };

    document.addEventListener("visibilitychange", checkIfVisible);
    window.addEventListener("focus", checkIfVisible);
    window.addEventListener("online", checkIfVisible);
    const timer = window.setInterval(checkIfVisible, VERSION_CHECK_INTERVAL_MS);

    return () => {
      stopped = true;
      inFlight?.abort();
      document.removeEventListener("visibilitychange", checkIfVisible);
      window.removeEventListener("focus", checkIfVisible);
      window.removeEventListener("online", checkIfVisible);
      window.clearInterval(timer);
    };
  }, [newBuildLive]);

  const dismiss = useCallback(() => setDismissed(true), []);

  const reason: NewVersionReason | null = dismissed
    ? null
    : loadFailed
      ? "load-failure"
      : newBuildLive
        ? "new-version"
        : null;

  return { reason, dismiss };
}
